import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CheckCircle2, ChevronRight, ArrowUpRight, Phone, MessageCircle, Mail } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { serviceHeroAbsolute, getServiceHero } from "@/content/service-pillars/hero-images";
import { SmartImage } from "@/components/SmartImage";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { EN_SERVICES_META } from "@/content/en-services/meta";
import { loadEnService } from "@/content/en-services/lazy.gen";
import type { EnServicePillar } from "@/content/en-services/types";
import { EnHeader, EnCtaStrip, EnOfficesStrip } from "./index";
import { NOTARY_KEYWORDS_TOP as NOTARY_KEYWORDS } from "@/content/notary-corpus/top.gen";
import { FaqSourceNote, type FaqSourceTopic } from "@/components/FaqSourceNote";
import { EnServiceKnowledgeLinks } from "@/components/EnServiceKnowledgeLinks";
import { PageGallery } from "@/components/PageGallery";

/** Map an EN pillar category to the matching official-source citation set. */
function enSourceTopic(category: EnServicePillar["category"]): FaqSourceTopic {
  if (category === "notary") return "notary";
  if (category === "translation") return "translation";
  if (category === "background") return "police-clearance";
  return "consular";
}

export const Route = createFileRoute("/en/services/$service")({
  loader: async ({ params }): Promise<{ pillar: EnServicePillar }> => {
    const p = await loadEnService(params.service);
    if (!p) throw notFound();
    return { pillar: p };
  },
  head: ({ loaderData }) => {
    const p = loaderData?.pillar;
    if (!p) return { meta: [{ title: "Service not found" }] };
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: p.metaTitle,
      description: p.metaDesc,
      path: `/en/services/${p.slug}`,
      type: "article",
      locale: "en_US",
      image: p.heroImage ?? serviceHeroAbsolute(p.category, site),
      imageAlt: p.heroImage ? p.h1 : getServiceHero(p.category).altEn,
      alternates: [{ lang: "th", path: `/services/${p.thaiSlug}` }],
      keywords: p.slug.includes("notary") ? NOTARY_KEYWORDS.slice(0, 60) : undefined,
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: "/en" },
              { name: "Services", url: "/en/services" },
              { name: p.h1, url: `/en/services/${p.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/en/services/${p.slug}#service`,
            name: p.h1,
            serviceType: p.heroEyebrow,
            description: p.tldr,
            url: `${site}/en/services/${p.slug}`,
            areaServed: { "@type": "Place", name: "Phuket, Thailand" },
            provider: { "@id": `${site}/#organization` },
            offers: {
              "@type": "Offer",
              priceCurrency: "THB",
              price: p.priceFrom,
              ...(p.priceTo && {
                priceSpecification: {
                  "@type": "PriceSpecification",
                  minPrice: p.priceFrom,
                  maxPrice: p.priceTo,
                  priceCurrency: "THB",
                },
              }),
            },
            availableLanguage: ["en", "th", "ru", "zh", "de", "fr"],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: p.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: p.h1,
            description: p.tldr,
            totalTime: "PT1H",
            estimatedCost: {
              "@type": "MonetaryAmount",
              currency: "THB",
              value: p.priceFrom,
            },
            tool: [
              { "@type": "HowToTool", name: "Passport / ID" },
              { "@type": "HowToTool", name: "Original document" },
            ],
            step: p.process.map((s) => ({
              "@type": "HowToStep",
              position: s.step,
              name: s.title,
              text: s.body,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${site}/en/services/${p.slug}#webpage`,
            url: `${site}/en/services/${p.slug}`,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "#tldr", "#pricing", "#faq"],
              xpath: ["/html/head/title"],
            },
          }),
        },
        ...(p.pricing && p.pricing.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "OfferCatalog",
                  name: `${p.h1} — Price list`,
                  itemListElement: p.pricing.map((row, i) => ({
                    "@type": "Offer",
                    position: i + 1,
                    name: row.item,
                    price:
                      typeof row.price === "string"
                        ? row.price.replace(/[^\d]/g, "").slice(0, 6) || undefined
                        : row.price,
                    priceCurrency: "THB",
                    description: row.note,
                    availability: "https://schema.org/InStock",
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: EnServicePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <EnHeader />
      <div className="p-16 text-center">
        <h1 className="text-2xl font-bold">Service not found</h1>
        <Link to="/en/services" className="mt-4 inline-block text-gold">
          ← All English services
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => {
    if (typeof console !== "undefined") console.error("EN service page error:", error);
    return (
      <div className="min-h-screen bg-background">
        <EnHeader />
        <div className="p-16 text-center">
          <h1 className="text-xl font-bold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">Please try again.</p>
          <a href="/en/services" className="mt-4 inline-block text-gold">
            ← Back to services
          </a>
        </div>
      </div>
    );
  },
});

function EnServicePage() {
  const data = Route.useLoaderData() as { pillar: EnServicePillar };
  const pillar = data.pillar;
  const related = EN_SERVICES_META.filter(
    (s) => s.slug !== pillar.slug && pillar.relatedEnSlugs?.includes(s.slug),
  );
  const siblings = EN_SERVICES_META.filter(
    (s) => s.slug !== pillar.slug && s.category === pillar.category,
  )
    .slice(0, 12)
    .map((s) => ({ slug: s.slug, h1: s.heroEyebrow }));

  return (
    <div className="min-h-screen bg-background text-foreground" lang="en">
      <EnHeader />

      {/* Breadcrumb */}
      <div className="border-b border-border bg-muted/30 py-3">
        <div className="mx-auto max-w-7xl px-4 text-xs text-muted-foreground">
          <Link to="/en" className="hover:text-gold">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/en/services" className="hover:text-gold">Services</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{pillar.heroEyebrow}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-br from-primary to-primary/80 py-16 text-primary-foreground md:py-20">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold">
            {pillar.heroEyebrow}
          </div>
          <h1 className="mt-4 max-w-4xl text-3xl font-extrabold leading-tight tracking-tight md:text-5xl">
            {pillar.h1}
          </h1>
          <p className="mt-5 max-w-3xl text-lg text-primary-foreground/90">{pillar.heroSubtitle}</p>
          <figure className="mt-8 max-w-3xl overflow-hidden rounded-2xl border border-primary-foreground/20">
            <SmartImage
              src={getServiceHero(pillar.category).src}
              alt={getServiceHero(pillar.category).altEn}
              width={1200}
              height={630}
              priority
              className="h-auto w-full object-cover"
            />
          </figure>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary hover:bg-gold/90"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a
              href="https://line.me/R/ti/p/@thainotary"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/10"
            >
              <MessageCircle className="h-4 w-4" /> LINE @THAINOTARY
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/10"
            >
              <Mail className="h-4 w-4" /> {COMPANY.email}
            </a>
          </div>
        </div>
      </section>

      {/* Quick Facts strip */}
      <section className="border-b border-border bg-background py-10">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl border border-gold/40 bg-gold/5 p-5 md:col-span-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">TL;DR</div>
              <p className="mt-2 text-sm leading-relaxed">{pillar.tldr}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">Pricing</div>
              <div className="mt-1 text-2xl font-black text-primary">
                ฿{pillar.priceFrom.toLocaleString()}
                {pillar.priceTo ? ` – ฿${pillar.priceTo.toLocaleString()}` : ""}
              </div>
              {pillar.priceUnit && (
                <div className="text-xs text-muted-foreground">{pillar.priceUnit}</div>
              )}
              <div className="mt-3 border-t border-border pt-3 text-xs">
                <div className="font-semibold text-gold">Turnaround</div>
                <div className="text-muted-foreground">{pillar.turnaround}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {pillar.quickFacts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                  {f.label}
                </div>
                <div className="mt-1 text-xs leading-relaxed">{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="border-b border-border bg-background py-16">
        <div className="mx-auto max-w-4xl px-4 space-y-14">
          {pillar.sections.map((s, idx) => (
            <article key={idx} className="prose-like">
              <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
                {s.h2}
              </h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-foreground/90">
                  {p}
                </p>
              ))}
              {s.bullets && s.bullets.length > 0 && (
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Documents */}
      <section className="border-b border-border bg-muted/30 py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
            Documents you'll need
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pillar.documents.map((g) => (
              <div key={g.group} className="rounded-2xl border border-border bg-card p-6">
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                  {g.group}
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {g.items.map((it) => (
                    <li key={it} className="flex gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-border bg-background py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
            How it works
          </h2>
          <ol className="mt-8 space-y-5">
            {pillar.process.map((s) => (
              <li key={s.step} className="flex gap-5 rounded-2xl border border-border bg-card p-5">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-black text-primary-foreground">
                  {s.step}
                </div>
                <div>
                  <div className="flex flex-wrap items-baseline gap-3">
                    <h3 className="text-base font-bold text-primary">{s.title}</h3>
                    {s.duration && (
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gold">
                        {s.duration}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Visual evidence (real work samples) */}
      <section className="border-b border-border bg-background py-16">
        <div className="mx-auto max-w-5xl px-4">
          <PageGallery category={pillar.category} seed={pillar.slug} locale="en" />
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border bg-muted/30 py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
            Frequently asked questions
          </h2>
          <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
            {pillar.faqs.map((f) => (
              <details key={f.q} className="group p-6">
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-sm font-bold">
                  {f.q}
                  <span className="text-gold transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      {related.length > 0 && (
        <section className="border-b border-border bg-background py-16">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="text-2xl font-extrabold tracking-tight text-primary md:text-3xl">
              Related services
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/en/services/$service"
                  params={{ service: r.slug }}
                  className="group flex flex-col rounded-2xl border border-border bg-card p-5 hover:-translate-y-1 hover:border-gold hover:shadow-xl transition-all"
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">
                    {r.heroEyebrow}
                  </div>
                  <h3 className="mt-2 text-base font-bold text-primary group-hover:text-gold">
                    {r.h1}
                  </h3>
                  <p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground line-clamp-3">
                    {r.tldr}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-gold">
                    View service <ChevronRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <EnServiceKnowledgeLinks category={pillar.category} siblings={siblings} />

      {/* CTA */}
      <section className="bg-primary py-16 text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight md:text-4xl">{pillar.finalCta}</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary hover:bg-gold/90"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a
              href="https://line.me/R/ti/p/@thainotary"
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/10"
            >
              <MessageCircle className="h-4 w-4" /> LINE @THAINOTARY
            </a>
            <Link
              to="/services/$service"
              params={{ service: pillar.thaiSlug }}
              className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-background/10"
            >
              ภาษาไทย <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4">
        <FaqSourceNote topic={enSourceTopic(pillar.category)} locale="en" />
      </div>

      <EnOfficesStrip />
      <EnCtaStrip />
      <SiteFooter />
    </div>
  );
}